/*
 * useObservationFormDefaults — pure mapper from an observation document
 * (as returned by `apiService.getObservation()`) to RHF `defaultValues`.
 *
 * Issue #707: the form-field naming conventions used by ObservationSubmit
 * and ObservationEdit diverged because the hydration logic was duplicated
 * inline in each page. Centralizing it here is the single source of truth
 * — when a new field ships, both the create and edit flows pick it up.
 *
 * Shape produced:
 *   {
 *     observationName, projectId, assignedTo,
 *     targetName, ra, dec, epoch, priority,
 *     exposures: [{ filter, exposureTime, exposureCount, binning, gain,
 *                   offset, readoutMode }],
 *     constraints: { minAltitude, maxAirmass, moonSeparation,
 *                    moonAvoidanceWidth, moonDown,
 *                    maxMoonPhase, maxClouds, twilightType,
 *                    startTime, endTime, baselineBefore, baselineAfter },
 *     guidingEnabled, requireGuiding, ditherEnabled,
 *     useDefaults, initialAutofocus,
 *     observationType, description,
 *   }
 *
 * Camel-case form-field names (`exposureTime`, `maxAirmass`) are what
 * react-hook-form's existing step components expect; the snake_case keys
 * on the server document (`exposure_time`, `maximum_airmass`) are the
 * persistence shape. This function bridges the two.
 */

import { useMemo } from 'react';
import { formatRAasHMS, formatDecAsDMS } from '../../../../utils/coordinateFormatting';
// #1165: single canonical moon-avoidance default.
import { MOON_PEAK_DEFAULT, MOON_WIDTH_DEFAULT } from '../../../../constants/moon';

// #1414: coerce a server date value (ISO string, epoch, or Date) into a valid
// Date for the MUI date pickers, or null. Crucially, an unparseable value maps
// to null rather than an Invalid Date — a raw string or Invalid Date reaching a
// picker makes MUI's date adapter throw "RangeError: Invalid time value",
// which crashes the whole ConstraintsStep / edit form.
function toDate(v) {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const EMPTY_DEFAULTS = Object.freeze({
  observationName: '',
  projectId: '',
  assignedTo: '',
  description: '',
  // Default matches what ConstraintsStep's observation_type selector expects
  // and what ObservationSubmit creates ('flexible' = optimal scheduling within
  // a date range). 'standard' was a legacy fallback that isn't a valid enum.
  observationType: 'flexible',
  // Time-based observation defaults (Stage 2). ConstraintsStep renders these
  // conditionally on observationType === 'time_based'.
  minimumObservingTimeMinutes: 30,
  desiredObservingTimeMinutes: 60,
  fillTime: false,
  // Fixed-time observation defaults (Stage 2). Conditional on 'fixed_time'.
  fixedTimeStart: null,
  fixedTimeEnd: null,
  // #937: UI-only discriminator, never stored. Derived from fill_time on load (see below), and it
  // derives fill_time back on change. 'exposures' matches the fill_time:false schema default.
  fixedTimeMode: 'exposures',
  startToleranceMinutes: 0,
  // Rise-to-set observation defaults. Conditional on 'rise_to_set'.
  riseToSetDate: null,
  riseToSetMinAltitude: 30,
  riseToSetTwilightType: 'astronomical',
  riseToSetSafetyMargin: 0,
  // Global repeat (cycle through all targets N times). Per-target
  // sequenceRepeatCount lives inside each entry of `targets` below.
  globalRepeatCount: 1,
  // Monitoring / cadence (ConstraintsStep watches these). Conditional on
  // 'monitoring'. Hydrated from cadence_* in buildObservationFormDefaults.
  cadenceDays: 1,
  cadenceStartDate: null,
  cadenceEndDate: null,
  cadenceMaxObservations: null,
  // Repetition config (Stage 3). RepetitionStep watches these.
  isRepetitive: false,
  repetitionStartDate: null,
  repetitionEndDate: null,
  repetitionIntervalDays: 0,
  repetitionIntervalHours: 0,
  repetitionWindowHours: 2,
  repetitionMaxExecutions: null,
  repetitionSkipIfDelayedHours: null,
  repetitionPriorityBoostPerDay: 0,
  // Multi-target shape (Stage 4): UnifiedTargetStep manages this via
  // useFieldArray. Each entry holds its own RA/Dec/exposures/etc.
  targets: [],
  priority: 5,
  // Flat aliases of the constraint fields, mirroring `constraints` below. ConstraintsStep
  // registers flat names; see the note in buildObservationFormDefaults.
  minAltitude: 30,
  maxAirmass: 2.0,
  moonSeparation: MOON_PEAK_DEFAULT,
  moonAvoidanceWidth: MOON_WIDTH_DEFAULT,
  moonDown: false,
  maxMoonPhase: 100,
  maxClouds: 30,
  twilightType: 'nautical',
  constraints: {
    minAltitude: 30,
    maxAirmass: 2.0,
    // #1157: the requirement at full moon (Lorentzian peak), its half-width in days, and the
    // moon-below-horizon override.
    moonSeparation: MOON_PEAK_DEFAULT,
    moonAvoidanceWidth: MOON_WIDTH_DEFAULT,
    moonDown: false,
    maxMoonPhase: 100,
    maxClouds: 30,
    twilightType: 'nautical',
    startTime: null,
    endTime: null,
    baselineBefore: 60,
    baselineAfter: 60,
  },
  guidingEnabled: true,
  requireGuiding: false,
  ditherEnabled: true,
  // Autofocus: AutofocusStep watches all of these. Defaults mirror what
  // ObservationSubmit sets at packages/user-gui/src/pages/user/ObservationSubmit.js:99-114.
  useDefaults: true,
  afEnabled: true,
  initialAutofocus: true,
  afTimeBased: true,
  afTimeInterval: 30,
  afTempBased: false,
  afTempThreshold: 2.0,
  afHfrBased: false,
  afHfrPercentage: 5,
  afFilterChange: false,
  afAfterExposures: false,
  afAfterExposuresCount: 10,
  // Metadata
  notes: '',
  isFastMover: false,
});

function mapExposure(ec) {
  if (!ec || typeof ec !== 'object') return null;
  return {
    filter: ec.filter ?? 'L',
    exposureTime: ec.exposure_time ?? 300,
    exposureCount: ec.exposure_count ?? 1,
    binning: String(ec.binning ?? 1),
    gain: ec.gain ?? null,
    offset: ec.offset ?? null,
    readoutMode: ec.readout_mode ?? null,
  };
}

/**
 * Map one server target document → form-state target shape consumed by
 * UnifiedTargetStep. Per-target ID is preserved so round-trips don't churn it.
 */
function mapTarget(t) {
  if (!t || typeof t !== 'object') return null;
  // RA/Dec hydrate as sexagesimal STRINGS (HH:MM:SS / ±DD:MM:SS). UnifiedTargetStep
  // expects strings (it calls .includes(':') to detect format) and shows the
  // alt-format readout as a hint. parseRA/parseDec convert back to decimal at
  // submit time. Numeric input from older docs is converted, '' stays ''.
  const raStr = (typeof t.ra === 'number' && !isNaN(t.ra))
    ? formatRAasHMS(t.ra)
    : (t.ra ?? '');
  const decStr = (typeof t.dec === 'number' && !isNaN(t.dec))
    ? formatDecAsDMS(t.dec)
    : (t.dec ?? '');
  return {
    id: t.id ?? undefined,
    name: t.name ?? '',
    naif_id: t.naif_id ?? '',   // #1409: repopulate the spacecraft NAIF id on edit-load so a round-trip preserves it
    ra: raStr,
    dec: decStr,
    epoch: t.epoch ?? 'J2000',
    rotation: t.rotation ?? 0,
    priority: t.priority ?? 5,
    sequenceRepeatCount: t.sequence_repeat_count ?? 1,
    exposureConfigs: (t.exposure_configs ?? []).map(mapExposure).filter(Boolean),
  };
}

function mapConstraints(c) {
  if (!c || typeof c !== 'object') return EMPTY_DEFAULTS.constraints;
  return {
    minAltitude: c.minimum_altitude ?? 30,
    maxAirmass: c.maximum_airmass ?? 2.0,
    // #1157: these must round-trip. Without them the editor repopulated the moon controls from
    // hardcoded defaults, so saving ANY unrelated field silently overwrote the stored moon
    // parameters. Peak+width maps 1:1 both ways, which the old anchor-day form could not.
    moonSeparation: c.moon_separation ?? MOON_PEAK_DEFAULT,
    moonAvoidanceWidth: c.moon_avoidance_width ?? MOON_WIDTH_DEFAULT,
    moonDown: c.moon_down ?? false,
    maxMoonPhase: c.maximum_moon_phase ?? 100,
    maxClouds: c.maximum_clouds ?? 30,
    twilightType: c.twilight_type ?? 'nautical',
    // #1414: these must be Date objects (or null) for the DatePickers — the raw
    // ISO string from the server crashes MUI with "Invalid time value".
    startTime: toDate(c.start_time),
    endTime: toDate(c.end_time),
    baselineBefore: c.baseline_before ?? 60,
    baselineAfter: c.baseline_after ?? 60,
  };
}

function mapGuiding(g) {
  // Two field-name eras in the wild:
  //   legacy:  guiding.enabled (bool)
  //   current: guiding_options.{disable_guiding, force_guiding, dither_enabled}
  // Prefer the current shape, fall back to legacy. disable_guiding=true
  // explicitly overrides any "enabled" we might see in the legacy field.
  const g_opts = g?.guiding_options ?? {};
  const legacy = g?.guiding ?? {};
  return {
    guidingEnabled: g_opts.disable_guiding === true
      ? false
      : (g_opts.enabled !== false && legacy.enabled !== false),
    requireGuiding: g_opts.force_guiding === true,
    ditherEnabled: g_opts.dither_enabled !== false,
  };
}

/**
 * Build RHF defaultValues from a server observation document.
 * Pass `null` (or omit) for create mode — yields safe empty defaults.
 */
export function buildObservationFormDefaults(observation) {
  if (!observation) {
    // Return a deep clone so caller can mutate without poisoning the singleton.
    return JSON.parse(JSON.stringify(EMPTY_DEFAULTS));
  }

  // Multi-target (Stage 4): hydrate every target. Fall back to the legacy
  // singular `target` field if the document predates the targets-array shape.
  const targetsRaw = (observation.targets?.length ?? 0) > 0
    ? observation.targets
    : (observation.target ? [observation.target] : []);
  const targets = targetsRaw.map(mapTarget).filter(Boolean);

  const af = observation.autofocus_options ?? {};
  const afTriggers = af.trigger_overrides ?? {};

  return {
    observationName: observation.name ?? '',
    // Both project_id and assigned_to may arrive populated as objects from a
    // .populate() chain on the server. Extract the _id so the Select can
    // match a MenuItem value.
    projectId: observation.project_id
      ? (typeof observation.project_id === 'object'
          ? observation.project_id._id
          : observation.project_id)
      : '',
    assignedTo: observation.assigned_to
      ? (typeof observation.assigned_to === 'object'
          ? observation.assigned_to._id
          : observation.assigned_to)
      : '',
    description: observation.description ?? '',
    targets,
    observationType: observation.observation_type ?? 'flexible',
    // Stage 2: time-based / fixed-time / rise-to-set fields. Each is a no-op
    // unless observationType matches the relevant value.
    minimumObservingTimeMinutes: observation.minimum_observing_time_minutes ?? 30,
    desiredObservingTimeMinutes: observation.desired_observing_time_minutes ?? 60,
    fillTime: observation.fill_time === true,
    fixedTimeStart: toDate(observation.fixed_time_start),
    // A server-derived end is not the user's; showing it would invite them to "keep" a window they
    // never chose. Round-trips as null, and the server re-derives it (#937).
    fixedTimeEnd: observation.fixed_time_end_source !== 'derived'
      ? toDate(observation.fixed_time_end)
      : null,
    // #937: derive the UI-only mode from persisted state, mirroring AutofocusStep's `useDefaults`.
    // A rise_to_set-converted observation lands here as fill_time:true -> 'fill', which is correct.
    fixedTimeMode: observation.fill_time === true ? 'fill' : 'exposures',
    startToleranceMinutes: observation.start_tolerance_minutes ?? 0,
    riseToSetDate: toDate(observation.metadata?.rise_to_set_date),
    riseToSetMinAltitude: observation.metadata?.rise_to_set_min_altitude ?? 30,
    riseToSetTwilightType: observation.metadata?.rise_to_set_twilight_type ?? 'astronomical',
    riseToSetSafetyMargin: observation.metadata?.rise_to_set_safety_margin ?? 0,
    // Stage 3: hydrate repetition_config (subdocument on the observation).
    // Updates go through the dedicated PUT /observations/:id/repetition-config
    // endpoint, not the standard PUT.
    isRepetitive: observation.repetition_config?.enabled === true,
    repetitionStartDate: toDate(observation.repetition_config?.start_date),
    repetitionEndDate: toDate(observation.repetition_config?.end_date),
    repetitionIntervalDays: observation.repetition_config?.interval_days ?? 0,
    repetitionIntervalHours: observation.repetition_config?.interval_hours ?? 0,
    repetitionWindowHours: observation.repetition_config?.window_hours ?? 2,
    repetitionMaxExecutions: observation.repetition_config?.max_executions ?? null,
    repetitionSkipIfDelayedHours: observation.repetition_config?.skip_if_delayed_hours ?? null,
    repetitionPriorityBoostPerDay: observation.repetition_config?.priority_boost_per_day ?? 0,
    // Monitoring / cadence (conditional on observationType === 'monitoring').
    // These were previously never hydrated, so editing a monitoring
    // observation loaded the required cadence start/end dates EMPTY — the
    // ConstraintsStep required-validation then blocked save (silently, before
    // the onInvalid handler was added). Hydrate from the cadence_* fields.
    cadenceDays: observation.cadence_days ?? 1,
    cadenceStartDate: toDate(observation.cadence_start_date),
    cadenceEndDate: toDate(observation.cadence_end_date),
    cadenceMaxObservations: observation.cadence_max_observations ?? null,
    priority: observation.priority ?? 5,
    globalRepeatCount: observation.global_repeat_count ?? 1,
    constraints: mapConstraints(observation.constraints),
    // ConstraintsStep registers its fields FLAT (`moonSeparation`, `minAltitude`, ...)
    // while this hook nests them under `constraints` and useObservationSubmit reads them
    // from there. On the edit page the two never met: the step's Controllers fell back to
    // their own hardcoded defaultValue props instead of the stored values, and anything the
    // user changed was dropped on save because the nested copy was what got written.
    // Emitting both shapes makes the step hydrate correctly; useObservationSubmit prefers
    // the flat value and falls back to the nested one. (#1157 -- the moon fields made this
    // visible, but it applies to every constraint the step renders.)
    ...mapConstraints(observation.constraints),
    ...mapGuiding(observation),
    // Autofocus: useDefaults=true when nothing per-observation is set.
    useDefaults: Object.keys(af).length === 0,
    afEnabled: af.enabled ?? true,
    initialAutofocus: af.initial_autofocus !== false,
    afTimeBased: afTriggers.time_based?.enabled ?? true,
    afTimeInterval: afTriggers.time_based?.interval_minutes ?? 30,
    afTempBased: afTriggers.temperature_based?.enabled ?? false,
    afTempThreshold: afTriggers.temperature_based?.threshold_celsius ?? 2.0,
    afHfrBased: afTriggers.hfr_based?.enabled ?? false,
    afHfrPercentage: afTriggers.hfr_based?.increase_percentage ?? 5,
    afFilterChange: afTriggers.filter_change?.enabled ?? false,
    afAfterExposures: afTriggers.after_exposures?.enabled ?? false,
    afAfterExposuresCount: afTriggers.after_exposures?.exposure_count ?? 10,
    // Metadata stored as Mixed on the document; we expose the two known
    // user-facing fields. Anything else in observation.metadata round-trips
    // via observation reference (Edit's save merges into existing metadata).
    notes: observation.metadata?.notes ?? '',
    isFastMover: observation.metadata?.is_fast_mover === true,
  };
}

/**
 * React hook wrapper. Memo-stabilized so RHF's `defaultValues` doesn't
 * change identity across renders for the same observation reference.
 */
export function useObservationFormDefaults(observation) {
  return useMemo(() => buildObservationFormDefaults(observation), [observation]);
}

export default useObservationFormDefaults;
